import React, { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import api from "@/lib/api";

function Line({ data, color, h = 120, baseline }) {
  if (!data || data.length < 2) return <div className="text-[11px] text-tc-muted py-6 text-center">Not enough data yet.</div>;
  const min = Math.min(...data, baseline ?? Math.min(...data));
  const max = Math.max(...data, baseline ?? Math.max(...data));
  const rng = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - ((v - min) / rng) * 100}`).join(" ");
  const by = baseline != null ? 100 - ((baseline - min) / rng) * 100 : null;
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ height: h }} className="w-full">
      {by != null && <line x1="0" y1={by} x2="100" y2={by} stroke="rgba(148,163,184,0.3)" strokeWidth="0.4" strokeDasharray="2" />}
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Stat({ label, value, accent }) {
  return <div className="tc-card p-3"><div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted">{label}</div><div className={`font-heading text-xl font-bold mt-0.5 ${accent || ""}`}>{value}</div></div>;
}

export default function RiskProfile() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/risk/profile").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="p-8 text-tc-muted font-mono text-sm">Loading risk profile…</div>;
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-5" data-testid="risk-profile">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-tc-coach/15 border border-tc-coach/30 text-tc-coach flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
        <div><h1 className="font-heading text-2xl font-bold">Risk Profile</h1><div className="text-[11px] font-mono text-tc-muted">{d.progress.rank} · Level {d.progress.level}</div></div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Avg Planned Risk" value={`${d.avg_planned_risk}%`} accent={d.risk_consistent ? "text-tc-bull" : "text-tc-xp"} />
        <Stat label="Risk Consistency" value={d.risk_consistent ? "Consistent" : "Variable"} accent={d.risk_consistent ? "text-tc-bull" : "text-tc-xp"} />
        <Stat label="Avg R" value={`${d.avg_r}R`} />
        <Stat label="Max Drawdown" value={`${d.max_drawdown}%`} accent="text-tc-bear" />
        <Stat label="Avg Winner" value={`+${d.avg_winner_r}R`} accent="text-tc-bull" />
        <Stat label="Avg Loser" value={`${d.avg_loser_r}R`} accent="text-tc-bear" />
        <Stat label="Avg Process Score" value={`${d.avg_process_score}/100`} accent="text-tc-coach" />
        <Stat label="PASS Quality" value={`${d.pass_quality}%`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="tc-card p-5" data-testid="equity-curve">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach mb-2">Simulated Equity Curve</div>
          <Line data={d.equity_curve} color="#06B6D4" baseline={10000} h={140} />
          <p className="text-[11px] text-tc-muted mt-2">Your simulated account balance across closed trades.</p>
        </div>
        <div className="tc-card p-5" data-testid="process-curve">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-mastery mb-2">Process Score Trend</div>
          <Line data={d.process_curve} color="#8B5CF6" baseline={70} h={140} />
          <p className="text-[11px] text-tc-muted mt-2">Decision quality over time. This — not the balance — is the real score.</p>
        </div>
      </div>

      <div className="tc-card p-5 border-tc-coach/20 bg-tc-coach/5 text-center">
        <p className="text-sm text-tc-sub">{d.note}</p>
      </div>

      {d.risk_violations?.length > 0 && (
        <div className="tc-card p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted mb-2">Risk Violations Logged</div>
          <div className="flex flex-wrap gap-2">
            {d.risk_violations.map((v) => <span key={v.code} className="text-[11px] font-mono px-2 py-1 rounded bg-tc-bear/10 text-tc-bear">{v.code.replace(/_/g, " ")} ×{v.count}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}
